import { ThemeProvider } from "@emotion/react";
import { CssBaseline, Grid } from "@mui/material";
import React from "react";
import theme from "../theme";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import StackKit from "@/shared/components/kit/stack";

type ContentLayoutProps = {
  children: React.ReactNode;
  icon?: React.ReactNode;
  title?: string;
  isImg?: boolean;
  detailTitle?: React.ReactNode | string;
  imgElement?: React.ReactNode;
  isContainerFluid?: boolean;
  isPadding?: boolean;
  sx?: any
  size?:any
};

/**
 * ContentLayout
 *
 * کامپوننت React برای ارائه یک لایه‌بندی انعطاف‌پذیر برای محتوای اصلی صفحات.
 * این کامپوننت شامل موارد زیر است:
 * - نمایش عنوان اصلی همراه با آیکون اختیاری
 * - نمایش عنوان فرعی یا توضیح اختیاری زیر عنوان اصلی
 * - محتوای اصلی (`children`) در یک کارت با پس‌زمینه و حاشیه‌های دلخواه
 * - نمایش تصویر اختیاری در کنار محتوای اصلی (در صورت عدم فعال بودن حالت Fluid)
 * - طراحی واکنش‌گرا با پشتیبانی از اندازه‌های مختلف صفحه
 *
 * ویژگی‌ها:
 * - استفاده از ThemeProvider برای اعمال تم سفارشی MUI
 * - CssBaseline برای ریست استایل‌های مرورگر
 * - StackKit و Grid برای مدیریت چیدمان و فاصله‌ها
 * - پشتیبانی از Props برای کنترل ظاهر و رفتار: `icon`, `title`, `detailTitle`, `imgElement`, `isContainerFluid`, `isPadding`, `sx`
 *
 * @param {ContentLayoutProps} props - پراپ‌های کامپوننت
 * @param {React.ReactNode} props.children - محتوای اصلی صفحه
 * @param {React.ReactNode} [props.icon] - آیکون اختیاری کنار عنوان
 * @param {string} [props.title] - عنوان اصلی
 * @param {React.ReactNode | string} [props.detailTitle] - عنوان فرعی یا توضیح
 * @param {React.ReactNode} [props.imgElement] - عنصر تصویر اختیاری در کنار محتوا
 * @param {boolean} [props.isContainerFluid] - اگر true باشد، محتوای اصلی تمام عرض را می‌گیرد
 * @param {boolean} [props.isPadding] - اگر true باشد، padding داخلی کارت حذف می‌شود
 * @param {any} [props.sx] - استایل‌های سفارشی برای container اصلی
 * @param {any} [props.size] - استایل‌های سفارشی برای container اصلی
 *
 * @returns JSX.Element
 *
 * @example
 * ```tsx
 * <ContentLayout
 *   icon={<SomeIcon />}
 *   title="صفحه داشبورد"
 *   detailTitle="خوش آمدید"
 *   imgElement={<img src="/example.png" alt="example" />}
 *   isContainerFluid={false}
 *   isPadding={false}
 *   size={12}
 * >
 *   <DashboardContent />
 * </ContentLayout>
 * ```
 */

const ContentLayout = (props: ContentLayoutProps) => {
  const { children, icon, title, detailTitle, imgElement, isContainerFluid, isPadding, sx ,size} = props;
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <StackKit sx={{ width: "100%", ...sx }}>
        <Grid container sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }} >
          <Grid size={12} sx={{ display: "flex", justifyContent: {sm:'start',xs:'center'}, alignItems: "center", gap: 1,paddingBottom:'20px'}}>
            <TypoGraphyKit variant="h6"> {icon}</TypoGraphyKit>
            <TypoGraphyKit variant="h5">{title}</TypoGraphyKit>
          </Grid>
          <Grid size={12}>{detailTitle}</Grid>
        </Grid>

        <Grid container gap={2} sx={{display: "flex",justifyContent: "space-between",alignItems: "center",}}>
          <Grid size={size ? size :{ md: 12, lg: isContainerFluid ? 12 : 8 }} sx={{bgcolor: "#434343",borderRadius: "1rem",padding: isPadding ? "0" : "50px 30px"}}>
            {children}
          </Grid>
          {isContainerFluid ? null : (
            <Grid size={3} sx={{ display: "flex", justifyContent: "center" }}>
              {imgElement}
            </Grid>
          )}
        </Grid>
      </StackKit>
    </ThemeProvider>
  );
};

export default ContentLayout;
